import { ArrowUpRight, Droplet, Lightbulb, Snowflake, Globe2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { HERO_IMAGES } from "@/data/products";
import { Marquee } from "@/components/Marquee";
import { DemoBanner } from "@/components/DemoBanner";
import { ScrollReveal } from "@/components/ScrollReveal";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";
import { trackMetaEvent } from "@/components/Analytics";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n?.toLocaleString("en-NZ");

const STATUS_TONE = {
  "Available": "text-emerald-400 border-emerald-400/40",
  "Build to Order": "text-orange-300 border-orange-300/40",
  "Coming Soon": "text-sky-300 border-sky-300/40",
};

const XSERIES_MARQUEE = [
  "500KG DYNAMIC LOAD",
  "TESTED TO 2.5 TONNES",
  "10-YEAR FRAME GUARANTEE",
  "TIG-WELDED ALUMINIUM",
  "MADE IN SOUTH AFRICA",
  "OEM-GRADE FINISH",
  "EXPEDITION TESTED",
];

const SPECS = [
  { k: "500kg", l: "Dynamic Load Capacity", n: "The strongest tray & canopy system on the NZ market, by far" },
  { k: "75L", l: "Onboard Water", n: "Integrated water storage system" },
  { k: "Africa", l: "Tested", n: "Proven in severe off-road conditions" },
  { k: "Global", l: "Expedition Ready", n: "Suited to round-the-world travel" },
];

const PACKAGES = [
  { icon: Droplet, t: "75L Onboard Water", d: "Integrated tank for true remote travel." },
  { icon: Lightbulb, t: "National Luna Lighting", d: "Class-leading 12v lighting system." },
  { icon: Snowflake, t: "Insulation & Fitout", d: "Thermal insulation and internal fitout options." },
  { icon: Globe2, t: "Overland Packages", d: "Far superior South African roof tent & awning packages, suited to vehicles in this class." },
];

export default function XSeriesTrayPage() {
  const [models, setModels] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/xseries`).then((res) => setModels(res.data)).catch((err) => { console.error(err); setModels([]); });
    trackMetaEvent("ViewContent", {
      content_ids: ["xseries-tray"],
      content_name: "X-Series Tray Canopy",
      content_type: "product",
      currency: "NZD",
    });
  }, []);

  return (
    <div data-testid="xseries-page">
      <SEO
        path="/x-series-tray"
        title="Bushtech X-Series Tray & Canopy NZ"
        description="Expedition-grade tray & canopy service body. 500kg dynamic load. 75L onboard water. Tested in Africa. Built for serious 4WD, overland and fleet touring use. From $28,990."
        image={HERO_IMAGES.xseries}
      />
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <video
            data-testid="xseries-hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={HERO_IMAGES.xseries}
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 35%' }}
            onTimeUpdate={(e) => {
              const t = e.currentTarget.currentTime;
              if (window.innerWidth < 640 && t >= 34) {
                e.currentTarget.currentTime = 0;
              } else if (window.innerWidth >= 640 && t >= 58) {
                e.currentTarget.currentTime = 0;
              }
            }}
          >
            <source src={HERO_IMAGES.xseriesVideo1080} type="video/mp4" media="(min-width: 1280px)" />
            <source src={HERO_IMAGES.xseriesVideo720} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-8% via-black/10 via-35% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/5 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-20 pt-32 w-full">
          <p className="overline mb-6">— X-Series · Service Body · Expedition Grade</p>
          <h1 className="font-display text-[12vw] md:text-[9vw] lg:text-[7.5vw] leading-[0.85] text-white hero-text-shadow max-w-[16ch]">
            A GENERATIONAL<br />
            <span className="text-[var(--brand-primary)]">LEAP</span> BEYOND<br />
            CONVENTIONAL DESIGN.
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
            <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/80 leading-relaxed max-w-xl [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
              Proprietary aluminium extrusions deliver exceptional strength with minimal weight. Tested in Africa under severe off-road conditions. Built for serious expedition, touring & commercial overland use.
            </p>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <a href="#models" data-testid="xseries-cta-models" className="btn-primary">Available Models <ArrowUpRight size={14} /></a>
              <a href="#enquire" className="btn-secondary">Build Enquiry</a>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={XSERIES_MARQUEE} />

      {/* SPLIT NARRATIVE */}
      <section className="container-x py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="overline mb-6">— Proven Globally</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] text-white mb-8">
              BUILT FOR<br />THE WORST<br /><span className="text-[var(--brand-primary)]">THE WORLD CAN THROW.</span>
            </h2>
            <div className="space-y-5 text-[var(--text-secondary)] text-lg leading-relaxed">
              <p>
                Bushtech builds expedition-grade aluminium canopies for vehicles and fleets operating in some of the harshest environments on Earth.
              </p>
              <p>
                That environment has shaped a construction philosophy centred on Bushtech's <span className="text-white">proprietary aluminium extrusions</span> — purpose-designed sections that deliver exceptional strength with minimal weight.
              </p>
              <p>
                A generational leap beyond conventional locally produced aluminium canopy designs.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative aspect-[4/3] overflow-hidden border border-[var(--border-default)]">
              <img src={HERO_IMAGES.xseriesDetail} alt="X-Series detail" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* SPECS BENTO */}
      <section className="container-x pb-24">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {SPECS.map((s, i) => (
            <div key={i} data-testid={`xseries-spec-${i}`} className="bg-[var(--bg-main)] p-8 md:p-10 hover:bg-[var(--bg-secondary)] transition-colors">
              <p className="font-display text-5xl md:text-7xl text-[var(--brand-primary)] leading-none">{s.k}</p>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-white mt-5">{s.l}</p>
              <p className="text-sm text-[var(--text-tertiary)] mt-2">{s.n}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECOND FULL-BLEED — scroll-reveal */}
      <ScrollReveal
        src={HERO_IMAGES.xseries3}
        alt="X-Series expedition build"
        className="h-[65vh] md:h-[95vh]"
        objectPosition="center center"
        mobileObjectPosition="70% center"
        overlay={<div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 via-25% to-transparent to-55%" />}
      >
        <div className="relative z-10 h-full flex items-end">
          <div className="container-x pb-16">
            <p className="overline mb-4">— Real World Use</p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] max-w-3xl hero-text-shadow">
              VALIDATED THROUGH<br />
              <span className="text-[var(--brand-primary)]">SOUTH ISLAND</span> OFF-ROAD TESTING.
            </h2>
          </div>
        </div>
      </ScrollReveal>

      {/* FULL BLEED IMAGE — scroll-reveal */}
      <ScrollReveal
        src={HERO_IMAGES.xseries2}
        alt="X-Series in terrain"
        className="h-[85vh]"
        objectPosition="center 60%"
        overlay={<div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 via-40% to-transparent to-75%" />}
      >
        <div className="relative z-10 h-full flex items-center">
          <div className="container-x">
            <p className="overline mb-6">— Beyond NZ Conditions</p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] max-w-3xl hero-text-shadow">
              MARKETED ELSEWHERE AS<br />"BUILT FOR NZ CONDITIONS".<br/>
              <span className="text-[var(--brand-primary)]">BUSHTECH BUILDS BEYOND THAT.</span>
            </h2>
            <p className="text-white/95 font-medium max-w-xl mt-6 leading-relaxed [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)]">
              The X-Series is engineered for environments far more demanding than those typically encountered in New Zealand — and fully suited to true long-range, international expedition use.
            </p>
          </div>
        </div>
      </ScrollReveal>

      {/* PACKAGES */}
      <section className="container-x py-24 md:py-32">
        <div className="mb-12">
          <p className="overline mb-4">— Full Overland Packages</p>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] max-w-3xl">
            BUILT TO LIVE OUT THERE.
          </h2>
          <p className="text-[var(--text-secondary)] mt-6 max-w-2xl leading-relaxed">
            XpeditioNZ specialises in fitting out serious touring vehicles with equipment engineered and torture-tested off-road in Africa — superior world brands proven in some of the toughest conditions all around the world.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {PACKAGES.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} data-testid={`xseries-package-${i}`} className="bg-[var(--bg-main)] p-8 hover:bg-[var(--bg-secondary)] transition-colors">
                <Icon size={28} className="text-[var(--brand-primary)] mb-6" strokeWidth={1.2} />
                <h3 className="font-display text-xl text-white tracking-wide">{p.t}</h3>
                <p className="text-[var(--text-secondary)] text-sm mt-3 leading-relaxed">{p.d}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-12 border border-[var(--border-default)] p-8 md:p-12 bg-[var(--bg-secondary)]">
          <p className="overline mb-4">— Coming Soon</p>
          <p className="font-display text-2xl md:text-3xl text-white max-w-3xl leading-tight">
            NEW GEN FORD RANGER · ISUZU D-MAX · INEOS QUARTERMASTER
          </p>
        </div>
      </section>

      {/* WALKAROUND VIDEO */}
      <section className="container-x pb-24">
        <div className="mb-10">
          <p className="overline mb-4">— See It In Person</p>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] max-w-3xl">
            HILUX <span className="text-[var(--brand-primary)]">WALKAROUND.</span>
          </h2>
        </div>
        <div className="relative aspect-video w-full max-w-5xl mx-auto border border-[var(--border-default)] overflow-hidden bg-black">
          <iframe
            data-testid="xseries-walkaround-video"
            src="https://www.youtube.com/embed/58FAif6dNQI?rel=0"
            title="Bushtech X-Series - Hilux walkaround"
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </section>

      <DemoBanner product="Bushtech X-Series" image={HERO_IMAGES.xseriesDemo} />

      {/* MODELS */}
      <section id="models" className="container-x pb-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="overline mb-4">— X-Series Builds</p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95]">
              AVAILABLE PLATFORMS.
            </h2>
          </div>
          <p className="text-[var(--text-secondary)] max-w-md font-mono text-sm">
            Trade, fleet & dealer enquiries welcome. Custom builds to specification.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {(models || []).map((m, i) => {
            const tone = STATUS_TONE[m.badge] || "text-white/60 border-white/20";
            return (
              <Link
                key={m.slug}
                to={`/x-series-tray/${m.slug}`}
                data-testid={`xseries-model-${m.slug}`}
                className="tech-card group relative block overflow-hidden"
              >
                <div className="aspect-[4/3] bg-[var(--bg-tertiary)] overflow-hidden relative">
                  <HoverImage primary={m.thumb} secondary={m.gallery?.[1]?.src} alt={m.name} />
                  <div className="absolute top-4 left-4">
                    <span className={`font-mono text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 border bg-black/60 backdrop-blur-sm ${tone}`}>
                      {m.badge}
                    </span>
                  </div>
                </div>
                <div className="p-5 flex flex-col gap-3">
                  <div>
                    <h3 className="font-display text-xl tracking-wide text-white leading-tight">{m.name}</h3>
                    <p className="text-xs text-[var(--text-tertiary)] mt-1 font-mono uppercase tracking-wider">{m.vehicle}</p>
                  </div>
                  <div className="flex items-end justify-between pt-3 border-t border-[var(--border-default)]">
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-tertiary)]">
                        {m.priceOnRequest ? "Price" : "From"}
                      </p>
                      <p className="font-display text-2xl text-[var(--brand-primary)] leading-none mt-1">
                        {m.priceOnRequest ? "Enquire" : `$${fmt(m.price)}`}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-white/70 group-hover:text-[var(--brand-primary)] transition-colors">
                      View <ArrowUpRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
